import { gql } from "graphql-request";
import { IMAGE_FRAGMENT, SEO_FRAGMENT } from "./fragments";

// Fetch SEO metadata for the Premier Residence main page
export const GET_SEO_DATA_PREMIER_RESIDENCE = gql`
  ${SEO_FRAGMENT}
  query PremierResidencePageSeo(
    $locale: I18NLocaleCode
    $status: PublicationStatus
  ) {
    premierResidences(status: $status, locale: $locale) {
      item_name
      seo {
        ...SEOProperties
      }
    }
  }
`;

// Fetch header details including contact info and logo images
export const PREMIER_RESIDENCE_HEADER = gql`
  ${IMAGE_FRAGMENT}
  query PremierResidenceHeader(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $documentId: ID!
  ) {
    headerDatasource(
      locale: $locale
      status: $status
      documentId: $documentId
    ) {
      documentId
      item_name
      mystays_hotel_label
      inquiry_label
      contact_phone
      logo_image_custom_page {
        ...ImageUrlProperties
      }
      logo_image {
        ...ImageUrlProperties
      }
    }
  }
`;

// Fetch navigation items for Premier Residence site
export const PREMIER_RESIDENCE_NAVIGATION = gql`
  query PremierResidenceNavigation(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $documentId: ID!
  ) {
    sharedDatasourceNavigationDatasource(
      locale: $locale
      status: $status
      documentId: $documentId
    ) {
      display_menu_items
      menu_items {
        item_name
        documentId
        show
        menu_title
        menu_link
      }
    }
  }
`;

// Fetch all Premier Residence pages (used for listing/map view)
export const PREMIER_RESIDENCE_PAGE = gql`
  ${IMAGE_FRAGMENT}
  query PremierResidencePages(
    $locale: I18NLocaleCode
    $status: PublicationStatus
  ) {
    premierResidencePages(
      locale: $locale
      status: $status
      pagination: { limit: -1 }
    ) {
      documentId
      item_name

      slider_images {
        home_slider_image_desktop {
          ...ImageUrlProperties
        }
        home_slider_image_mobile {
          ...ImageUrlProperties
        }
      }
      resident_information {
        id
        display_name
        home_banner_label
        latitude
        longitude
        google_place_id
      }
      home_thumbnail_section {
        id
        home_page_property_label
        thumbnail_image {
          ...ImageUrlProperties
        }
      }
    }
  }
`;

// Fetch reusable map pin image asset
export const PREMIER_RESIDENCE_MAP_IMG = gql`
  ${IMAGE_FRAGMENT}
  query PremierResidenceImg(
    $locale: I18NLocaleCode
    $status: PublicationStatus
  ) {
    reusables(locale: $locale, status: $status) {
      Images {
        map_pin {
          ...ImageUrlProperties
        }
      }
    }
  }
`;

// Fetch home page datasource — includes hero, facilities, and selector section
export const PREMIER_RESIDENCE_HOME_DATASOURCE = gql`
  query PremierResidenceHomeDatasource(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $documentId: ID!
  ) {
    premierResidenceHomeDatasource(
      locale: $locale
      status: $status
      documentId: $documentId
    ) {
      home_banner_datasource {
        display_name
        documentId
        heading
        item_name
        title
        details_button
        selected_residence
      }
      home_image_selector_datasource {
        item_name
        title
        description
      }
      top_facilities_and_properties_datasource {
        facilities {
          list_of_top_facilities
        }
      }
    }
  }
`;

// Fetch gallery images for the image selector section
export const PREMIER_RESIDENCE_IMAGE_SELECTOR = gql`
  ${IMAGE_FRAGMENT}
  query PremierResidenceImageSelector(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $documentId: ID!
  ) {
    homeImageSelectorDatasource(
      locale: $locale
      status: $status
      documentId: $documentId
    ) {
      item_name
      title
      description
      documentId
      gallery_images_mobile
      gallery_images_desktop
      premier_residence_home_datasources_slide_image {
        mobile(pagination: { limit: -1 }) {
          media {
            ...ImageUrlProperties
          }
          documentId
        }
        desktop(pagination: { limit: -1 }) {
          media {
            ...ImageUrlProperties
          }
          documentId
        }
      }
    }
  }
`;

// Fetch footer details like contact info, labels, and phone section
export const PREMIER_RESIDENCE_FOOTER_DATASOURCE = gql`
  query PremierResidenceFooterDatasource(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $documentId: ID!
  ) {
    footerDatasource(
      locale: $locale
      status: $status
      documentId: $documentId
    ) {
      contact_us_form {
        contact_form_css_class
        contact_form_label
        contact_us_link_label
      }
      phone_section {
        phone_css_class
        phone_call_us_label
        phone_number
        timings
      }
      labels {
        contact_us_heading
        mystays_hotel_header_text
        mystays_hotel_bottom_label
        copyright_label
      }
    }
  }
`;

// Fetch detailed info for a specific Premier Residence (main page + rooms + datasources)
export const GET_PREMIER_RESIDENCE_DETAILS = gql`
  ${IMAGE_FRAGMENT}
  query PremierResidenceDetails(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $premierResidenceName: String!
  ) {
    premierResidencePages(
      locale: $locale
      status: $status
      filters: { item_name: { eq: $premierResidenceName } }
    ) {
      item_name
      documentId
      resident_information {
        display_name
        home_banner_label
        latitude
        longitude
        contact_phone
        contact_email
        contact_email_cc
        address
      }
      home_thumbnail_section {
        home_page_property_label
        thumbnail_image {
          ...ImageUrlProperties
        }
      }
      slider_images {
        home_slider_image_desktop {
          ...ImageUrlProperties
        }
        home_slider_image_mobile {
          ...ImageUrlProperties
        }
      }
      list_of_rooms
      access_points {
        item_name
        text {
          heading
          description
          icon_css_class
        }
      }
      datasources {
        facilities_and_services_datasource {
          lists {
            list_of_facilities
          }
        }
        property_description_datasource {
          description_heading
          description_body
          list_of_features
          features {
            item_name
            documentId
            icon_css_class
            small_description
          }
        }
        property_price_datasource {
          header_label
          premier_label
          standard_label
          bottom_description
          promotion_rate_label
          premier_rooms
          standard_rooms
          top_description
          property_prices {
            item_name
            documentId
            room_name
            price
            promotion_price
          }
        }
      }
      rooms {
        documentId
        configuration {
          room_name
          size
          number_of_bathrooms
          number_of_rooms
          number_of_toilets
        }
        description {
          room_subheading
          room_description
          pricing_plan_heading
          pricing_plan
          price_list
          floor_plan {
            ...ImageUrlProperties
          }
        }
        images {
          gallery_image
          banner_image_desktop {
            ...ImageUrlProperties
          }
          banner_image_mobile {
            ...ImageUrlProperties
          }
        }
        room_images {
          item_name
          documentId
          images {
            description
            desktop_image {
              ...ImageUrlProperties
            }
            mobile_image {
              ...ImageUrlProperties
            }
          }
        }
      }
    }
  }
`;

// Fetch labels for the Residence contact form
export const GET_RESIDENCE_FORM = gql`
  query GetResidenceFormDatasource(
    $documentId: ID!
    $status: PublicationStatus
    $locale: I18NLocaleCode
  ) {
    residenceFormDatasource(
      documentId: $documentId
      status: $status
      locale: $locale
    ) {
      labels {
        attach_file_label
        checkin_label
        checkout_label
        company_name_label
        dates_and_request_heading
        email_label
        error_message
        first_name_label
        guest_number_label
        heading
        id
        last_name_label
        location_label
        phone_number_label
        requirements_label
        select_residence_text
        sub_heading
        submit_button_label
        success_message
      }
    }
  }
`;

// Fetch facilities content for a specific Premier Residence
export const GET_PREMIER_RESIDENCE_CONTENT = gql`
  query GetPremierResidenceContent(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $contentDocumentId: ID!
  ) {
    premierResidenceContent(
      documentId: $contentDocumentId
      locale: $locale
      status: $status
    ) {
      facilities(pagination: { limit: -1 }) {
        item_name
        documentId
        display_name
        displaying_name
        css_class
      }
    }
  }
`;

// Fetch all Premier Residence pages (used in residence listing dropdown)
export const GET_PREMIER_RESIDENCE_LIST = gql`
  query GetPremierResidenceList(
    $status: PublicationStatus
    $locale: I18NLocaleCode
  ) {
    premierResidences(status: $status, locale: $locale) {
      premier_residence_pages {
        resident_information {
          display_name
        }
        documentId
        item_name
      }
    }
  }
`;

// Fetch SEO data for a specific Premier Residence details page
export const GET_SEO_DATA_PREMIER_RESIDENCE_DETAILS = gql`
  ${SEO_FRAGMENT}
  query getSeoDataPremierResidenceDetails(
    $locale: I18NLocaleCode
    $status: PublicationStatus
    $premierResidenceName: String!
  ) {
    premierResidencePages(
      locale: $locale
      status: $status
      filters: { item_name: { eq: $premierResidenceName } }
    ) {
      item_name
      seo {
        ...SEOProperties
      }
    }
  }
`;
